/**
 * @fileoverview WEB-844 — reconstruct the line structure Ticketmaster's public
 * API strips out of venue/event policy copy.
 *
 * TM stores this copy WITH real newlines. Verified against TM's own page
 * payload for the same event: `generalInfo.text` carries 6 `\n` and `webInfo`
 * carries 8, and each is byte-identical to the Discovery API's `info` /
 * `pleaseNote` once its newlines are replaced by single spaces. So the
 * structure is destroyed at the public-API boundary, before it ever reaches
 * LOCI — it is not something we dropped, and not something we can request.
 *
 * We therefore RECONSTRUCT it: one sentence per line. Measured against TM's
 * own rendering of the reference event, this reproduces 6 of its 7 content
 * lines exactly, with two known and accepted losses:
 *
 *   1. Blank-line paragraph grouping is UNRECOVERABLE. Discovery collapsed
 *      `\n\n` to a single space, which is indistinguishable from the space
 *      between two sentences. Nothing downstream can tell them apart.
 *   2. A paragraph whose sentences TM kept on ONE line gets split. On the
 *      reference event the trailing sponsor paragraph becomes two lines.
 *
 * Do NOT "fix" (2) by loosening the boundary rule — the cases are genuinely
 * ambiguous in the flattened string, and a looser rule mis-splits ordinary
 * copy elsewhere. The trade is deliberate.
 *
 * Safety property: this only ever converts whitespace BETWEEN sentences into
 * a newline. No character of the source text is added, removed, reordered or
 * rewritten, so the rendered copy stays verbatim.
 */

/**
 * Split point: whitespace after sentence-ending punctuation, followed by
 * something that can begin a sentence. The trailing-context class excludes
 * lowercase deliberately — it keeps bare URLs (`… deductible. https://…`)
 * attached to the sentence that introduces them, which is how TM renders them.
 */
const SENTENCE_BOUNDARY = /(?<=[.!?])\s+(?=[A-Z0-9"'“])/;

/**
 * Tokens that end in a period without ending a sentence. Kept deliberately
 * small: every entry is a false-split we would otherwise produce on real
 * venue-policy copy (addresses, times, org suffixes).
 */
const ABBREVIATIONS = new Set([
  'a.m',
  'approx',
  'ave',
  'blvd',
  'co',
  'dept',
  'dr',
  'est',
  'etc',
  'ext',
  'inc',
  'jr',
  'ltd',
  'max',
  'min',
  'mr',
  'mrs',
  'ms',
  'no',
  'p.m',
  'rd',
  'sr',
  'st',
  'u.s',
  'vs',
]);

/**
 * True when `segment` ends with a token that is an abbreviation rather than a
 * sentence terminator — including any single letter, which covers initials.
 */
function endsWithAbbreviation(segment: string): boolean {
  const match = /([A-Za-z.]+)\.$/.exec(segment);
  if (match === null) {
    return false;
  }
  const token = match[1].toLowerCase();
  return token.length === 1 || ABBREVIATIONS.has(token);
}

function splitIntoSentences(text: string): string[] {
  const parts = text.split(SENTENCE_BOUNDARY);
  const sentences: string[] = [];

  for (const part of parts) {
    const previous = sentences[sentences.length - 1];
    // Re-join a split we should not have made, restoring the single space the
    // boundary consumed.
    if (previous !== undefined && endsWithAbbreviation(previous)) {
      sentences[sentences.length - 1] = `${previous} ${part}`;
      continue;
    }
    sentences.push(part);
  }

  return sentences;
}

/**
 * Format source policy copy for display.
 *
 * - Normalises CRLF/CR to `\n`.
 * - When the source ALREADY contains newlines they are source-attested
 *   structure and are preserved exactly — never re-split. Some TM fields
 *   (e.g. `accessibility.info`) do arrive with real `\r\n\r\n`, and
 *   re-splitting those would corrupt real formatting with synthetic breaks.
 * - Only a genuinely flat string is split one-sentence-per-line.
 *
 * Returns `''` for blank input so callers can gate on emptiness.
 */
export function formatPolicyText(raw: string): string {
  const normalized = raw.replace(/\r\n?/g, '\n').trim();

  if (normalized.length === 0) {
    return '';
  }

  if (normalized.includes('\n')) {
    return normalized;
  }

  return splitIntoSentences(normalized).join('\n');
}
